import React, { useCallback, useEffect, useState } from "react";
import {
  getPurchaseVendors,
  createPurchaseVendor,
  updatePurchaseVendor,
} from "../../api/service";

const EMPTY = { name: "", phone: "", gst_number: "", address: "", notes: "", is_one_time: false };

function VendorForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) {
      setErr("Vendor name is required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        name: f.name,
        phone: f.phone,
        gst_number: f.gst_number,
        address: f.address,
        notes: f.notes,
        is_one_time: f.is_one_time,
      };
      if (initial && initial.id) await updatePurchaseVendor(initial.id, body);
      else await createPurchaseVendor(body);
      onSaved();
    } catch (e) {
      setErr(e.message || "Could not save the vendor.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h6>{initial && initial.id ? "Edit vendor" : "Add vendor"}</h6>
        {err && <div className="alert alert-danger py-1">{err}</div>}
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label small mb-1">Name *</label>
            <input
              className="form-control form-control-sm"
              maxLength={120}
              value={f.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label small mb-1">Phone</label>
            <input
              className="form-control form-control-sm"
              maxLength={32}
              value={f.phone}
              onChange={(e) => set("phone", e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small mb-1">GST number</label>
            <input
              className="form-control form-control-sm"
              maxLength={32}
              value={f.gst_number}
              onChange={(e) => set("gst_number", e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label small mb-1">Address</label>
            <input
              className="form-control form-control-sm"
              maxLength={255}
              value={f.address}
              onChange={(e) => set("address", e.target.value)}
            />
          </div>
          <div className="col-md-9">
            <label className="form-label small mb-1">Notes</label>
            <input
              className="form-control form-control-sm"
              maxLength={500}
              value={f.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <div className="form-check">
              <input
                id="vendor-one-time"
                type="checkbox"
                className="form-check-input"
                checked={!!f.is_one_time}
                onChange={(e) => set("is_one_time", e.target.checked)}
              />
              <label htmlFor="vendor-one-time" className="form-check-label small">
                One-time vendor
              </label>
            </div>
          </div>
        </div>
        <div className="mt-3">
          <button type="button" className="btn btn-primary btn-sm me-2" disabled={busy} onClick={save}>
            {busy ? "Saving..." : "Save"}
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VendorsTab() {
  const [rows, setRows] = useState([]);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const h = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(h);
  }, [qInput]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (kind) params.one_time = kind;
    return getPurchaseVendors(params)
      .then((res) => {
        setRows(res?.results || []);
        setErr("");
      })
      .catch((e) => setErr(e.message || "Could not load vendors."))
      .finally(() => setLoading(false));
  }, [q, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (v, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      await updatePurchaseVendor(v.id, body);
      await load();
    } catch (e) {
      setErr(e.message || "Update failed.");
    }
  };

  return (
    <div>
      <div className="d-flex align-items-end justify-content-between mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-auto">
            <label className="form-label small mb-1">Search</label>
            <input
              className="form-control form-control-sm"
              placeholder="Vendor name"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
            />
          </div>
          <div className="col-auto">
            <label className="form-label small mb-1">Type</label>
            <select
              className="form-select form-select-sm"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">All</option>
              <option value="0">Regular</option>
              <option value="1">One-time</option>
            </select>
          </div>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({})}>
          + Add vendor
        </button>
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}

      {editing && (
        <VendorForm
          key={editing.id || "new"}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>GST</th>
              <th>Address</th>
              <th>Type</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id}>
                <td>{v.name}</td>
                <td>{v.phone || "-"}</td>
                <td>{v.gst_number || "-"}</td>
                <td>{v.address || "-"}</td>
                <td>
                  <span className={"badge " + (v.is_one_time ? "bg-warning text-dark" : "bg-success")}>
                    {v.is_one_time ? "One-time" : "Regular"}
                  </span>
                </td>
                <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm me-1"
                    onClick={() => setEditing(v)}
                  >
                    Edit
                  </button>
                  {v.is_one_time && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm me-1"
                      onClick={() => patch(v, { is_one_time: false })}
                    >
                      Make regular
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() =>
                      patch(
                        v,
                        { active: false },
                        "Deactivate " + v.name + "? It will no longer appear in the vendor list."
                      )
                    }
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  No vendors found.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
